"use client";

import { useEffect, useState } from "react";
import { FeedbackForm } from "./feedback-form";
import type {
  ClassMerge,
  FeedbackApiFetch,
  FeedbackComponents,
  FeedbackMessages,
  FeedbackMode,
  FeedbackSessionUser,
} from "./types";

export interface FeedbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: FeedbackMode;
  user: FeedbackSessionUser | null;
  messages: FeedbackMessages;
  apiFetch: FeedbackApiFetch;
  components: FeedbackComponents;
  cn: ClassMerge;
  /** Portal mode only — link sent to /login when session unexpectedly missing. */
  loginUrl?: string;
  /** Override panel classes (e.g. raise above bottom-tab-bar). */
  panelClassName?: string;
  /** Listen for `pontimed:open-feedback` CustomEvent on window, prefill + open. */
  listenForGlobalEvent?: boolean;
}

/**
 * Controlled feedback sheet — owns prefill state for the global
 * `pontimed:open-feedback` event but delegates open/close to the parent
 * (so triggers placed elsewhere on the page can drive the same sheet).
 *
 * The sheet shell is supplied by the host as `components.ResponsiveSheet`
 * — apps/web has its own (Next.js custom Dialog), apps/app has its own
 * (base-ui-backed). Both honor the visual contract via @pontimed/ui/sheet-kit.
 */
export function FeedbackSheet({
  open,
  onOpenChange,
  mode,
  user,
  messages,
  apiFetch,
  components,
  cn,
  loginUrl,
  panelClassName,
  listenForGlobalEvent = false,
}: FeedbackSheetProps) {
  const { ResponsiveSheet } = components;

  const [initialType, setInitialType] = useState<string | null>(null);
  const [initialDescription, setInitialDescription] = useState("");

  useEffect(() => {
    if (!listenForGlobalEvent) return;
    function handler(e: Event) {
      const detail = (e as CustomEvent<{ type?: string; description?: string }>).detail || {};
      if (detail.type) setInitialType(detail.type);
      if (detail.description) setInitialDescription(detail.description);
      onOpenChange(true);
    }
    window.addEventListener("pontimed:open-feedback", handler);
    return () => window.removeEventListener("pontimed:open-feedback", handler);
  }, [listenForGlobalEvent, onOpenChange]);

  return (
    <ResponsiveSheet
      open={open}
      onClose={() => onOpenChange(false)}
      desktop="float-right"
      title={messages.title}
      ariaLabel={messages.title}
      closeLabel={messages.close}
      panelClassName={panelClassName}
    >
      <FeedbackForm
        mode={mode}
        user={user}
        messages={messages}
        apiFetch={apiFetch}
        components={components}
        cn={cn}
        loginUrl={loginUrl}
        initialType={initialType}
        initialDescription={initialDescription}
        onSubmitted={() => {
          onOpenChange(false);
          setInitialType(null);
          setInitialDescription("");
        }}
      />
    </ResponsiveSheet>
  );
}
